/**
 * Scripted usability sessions (W3-05; WORKPACKAGES Appendix C H2: "5 first-time charters and 3 RPE users, scripted
 * tasks (place a pattern, animate a line, fix a problem, judged test play, export)"; tooling: task sheet + session
 * logging).
 *
 *  - The task sheet: `USABILITY_TASKS`, five tasks in order, each with the words the facilitator reads out, what to
 *    watch for, and a check that recognises success from the document and the screen (relative to the state when the
 *    task started), so the log records when the participant got there even if nobody pressed "Done".
 *  - The logger (`createUsabilityLogger`): started from the palette in the editor (`dev.usabilityStart`), it records
 *    per task the start, the first automatic success, the facilitator's outcome (done / gave up) and, while it runs:
 *    committed edits (label and command id), undo / redo, keys pressed outside text fields (chord only, never typed
 *    text), clicks per panel region, workspace and level changes, toasts the app showed (their message keys) and
 *    uncaught errors. It is saved to localStorage (`phiedit.usability.v1`) after every event, so a reload keeps it;
 *    the `#/dev/usability` page shows the sheet and saves the log as JSON for the facilitator.
 * Nothing leaves the browser.
 */

import type { AppServices } from '../../app/registry/types';
import type { ChartDoc } from '../../core/model/types';
import { snapshotDoc } from '../../core/doc/state';
import { Beats } from '../../core/time/beat';
import type { Unsubscribe } from '../../core/util/types';
import { m } from './noteLane.messages';

export const USABILITY_STORAGE_KEY = 'phiedit.usability.v1';

export type UsabilityTaskId = 'pattern' | 'animate' | 'fix' | 'testPlay' | 'export';
export type ParticipantProfile = 'firstTime' | 'rpe';

/** What a check compares against: the document and problem count when the task started. */
export interface TaskBaseline {
  readonly doc: ChartDoc | null;
  readonly problems: number;
}

export interface UsabilityTask {
  readonly id: UsabilityTaskId;
  /** Title and the words read out to the participant (message keys). */
  readonly title: string;
  readonly script: string;
  /** What the facilitator watches for, and what the automatic check counts as reached (message keys). */
  readonly observe: string;
  readonly success: string;
  /** True once the task's goal is reached (read from the document, the problems and the screen). */
  readonly check: (s: Pick<AppServices, 'store' | 'problems'>, base: TaskBaseline) => boolean;
}

function problemCount(s: Pick<AppServices, 'problems'>): number {
  try {
    const c = s.problems.counts();
    return c.fatal + c.error + c.warning;
  } catch {
    return 0;
  }
}

/** Notes placed since the baseline (ids not in it), and how many of them are holds. */
function newNotes(doc: ChartDoc, base: ChartDoc | null): { readonly count: number; readonly holds: number } {
  let count = 0;
  let holds = 0;
  for (const n of doc.notes.values()) {
    if (base?.notes.has(n.id) === true) continue;
    count++;
    if (n.kind === 'hold') holds++;
  }
  return { count, holds };
}

/** A position X segment of at least 100 px with a curve other than Linear, added or changed since the baseline. */
function animatedLine(doc: ChartDoc, base: ChartDoc | null): boolean {
  for (const tr of doc.tracks.values()) {
    if (tr.owner.kind !== 'line' || (tr.owner.prop !== 'moveX' && tr.owner.prop !== 'moveY')) continue;
    const keys = [...doc.keys.values()].filter((k) => k.track === tr.id).sort((a, b) => Beats.cmp(a.t, b.t));
    for (let i = 0; i + 1 < keys.length; i++) {
      const a = keys[i];
      const b = keys[i + 1];
      if (a === undefined || b === undefined) continue;
      if (typeof a.vOut !== 'number' || typeof b.vIn !== 'number') continue;
      const eased = a.ease.kind === 'bezier' || (a.ease.kind === 'tween' && a.ease.id !== 1);
      const old = base?.keys.get(a.id);
      const changed = old === undefined || old.ease !== a.ease || old.vOut !== a.vOut;
      if (eased && changed && Math.abs(b.vIn - a.vOut) >= 100) return true;
    }
  }
  return false;
}

export const USABILITY_TASKS: readonly UsabilityTask[] = [
  {
    id: 'pattern',
    title: m.uxTaskPattern,
    script: m.uxTaskPatternScript,
    observe: m.uxTaskPatternObserve,
    success: m.uxTaskPatternSuccess,
    check: (s, base) => {
      const doc = s.store?.doc;
      if (doc === undefined) return false;
      const n = newNotes(doc, base.doc);
      return n.count >= 8 && n.holds >= 1;
    },
  },
  {
    id: 'animate',
    title: m.uxTaskAnimate,
    script: m.uxTaskAnimateScript,
    observe: m.uxTaskAnimateObserve,
    success: m.uxTaskAnimateSuccess,
    check: (s, base) => {
      const doc = s.store?.doc;
      return doc !== undefined && animatedLine(doc, base.doc);
    },
  },
  {
    id: 'fix',
    title: m.uxTaskFix,
    script: m.uxTaskFixScript,
    observe: m.uxTaskFixObserve,
    success: m.uxTaskFixSuccess,
    check: (s, base) => base.problems > 0 && problemCount(s) < base.problems,
  },
  {
    id: 'testPlay',
    title: m.uxTaskTestPlay,
    script: m.uxTaskTestPlayScript,
    observe: m.uxTaskTestPlayObserve,
    success: m.uxTaskTestPlaySuccess,
    // The results card of a finished test play.
    check: () => typeof document !== 'undefined' && document.querySelector('[data-test-play] [data-finished]') !== null,
  },
  {
    id: 'export',
    title: m.uxTaskExport,
    script: m.uxTaskExportScript,
    observe: m.uxTaskExportObserve,
    success: m.uxTaskExportSuccess,
    check: (s, base) => {
      const last = s.store?.doc.editor.lastExport ?? null;
      const before = base.doc?.editor.lastExport ?? null;
      return last !== null && (before === null || last.at > before.at);
    },
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Log
// ---------------------------------------------------------------------------------------------------------------

export type UsabilityEventKind =
  'session' | 'task' | 'commit' | 'undo' | 'redo' | 'key' | 'click' | 'workspace' | 'level' | 'toast' | 'error';

export interface UsabilityEvent {
  /** Milliseconds since the session started. */
  readonly ms: number;
  readonly kind: UsabilityEventKind;
  readonly data: Readonly<Record<string, string | number | boolean | null>>;
}

export interface TaskRecord {
  readonly id: UsabilityTaskId;
  readonly startMs: number;
  /** First time the automatic check passed. */
  readonly reachedMs: number | null;
  readonly endMs: number | null;
  readonly outcome: 'done' | 'gaveUp' | null;
  readonly commits: number;
  readonly undos: number;
}

export interface UsabilityLog {
  readonly version: 1;
  readonly participant: string;
  readonly profile: ParticipantProfile;
  readonly startedAt: string;
  readonly userAgent: string;
  readonly level: string;
  readonly preset: string;
  readonly tasks: readonly TaskRecord[];
  readonly events: readonly UsabilityEvent[];
  readonly endedMs: number | null;
}

/** Most events kept (a long session of key presses stays well below this). */
const MAX_EVENTS = 20_000;

export interface UsabilityLogger {
  readonly log: UsabilityLog;
  readonly active: boolean;
  /** Index of the current task in USABILITY_TASKS, or -1 before the first / after the last. */
  readonly taskIndex: number;
  startTask(index: number): void;
  /** Ends the current task with the facilitator's outcome and starts the next one (if any). */
  endTask(outcome: 'done' | 'gaveUp'): void;
  stop(): void;
  subscribe(cb: () => void): Unsubscribe;
}

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

function save(log: UsabilityLog): void {
  try {
    localStorage.setItem(USABILITY_STORAGE_KEY, JSON.stringify(log));
  } catch {
    // private mode or quota: the session still runs; the facilitator can save the JSON from the page
  }
}

/** The last saved session (null when there is none or it cannot be read). */
export function loadUsabilityLog(): UsabilityLog | null {
  try {
    const raw = localStorage.getItem(USABILITY_STORAGE_KEY);
    if (raw === null) return null;
    const v: unknown = JSON.parse(raw);
    return typeof v === 'object' && v !== null && (v as { readonly version?: unknown }).version === 1
      ? (v as UsabilityLog)
      : null;
  } catch {
    return null;
  }
}

/** A chord as the log records it (modifiers and the physical key; never the produced text). */
export function chordOfEvent(e: Pick<KeyboardEvent, 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  parts.push(e.code);
  return parts.join('+');
}

function isTextTarget(t: EventTarget | null): boolean {
  if (typeof HTMLElement === 'undefined' || !(t instanceof HTMLElement)) return false;
  return t.isContentEditable || t.closest('input, textarea, select') !== null;
}

/**
 * Starts a session for `participant` and returns its logger (the caller keeps one at a time; `stop` detaches every
 * listener). `win` is the window whose keys, clicks and errors are recorded.
 */
export function createUsabilityLogger(
  services: AppServices,
  participant: string,
  profile: ParticipantProfile,
  win: Window | null = typeof window === 'undefined' ? null : window,
): UsabilityLogger {
  const t0 = now();
  const listeners = new Set<() => void>();
  const tasks: TaskRecord[] = [];
  let events: UsabilityEvent[] = [];
  let taskIndex = -1;
  let active = true;
  let endedMs: number | null = null;
  let base: TaskBaseline = { doc: null, problems: 0 };
  const offs: Unsubscribe[] = [];

  const st = services.editor.getState();
  const snapshot = (): UsabilityLog => ({
    version: 1,
    participant,
    profile,
    startedAt: new Date().toISOString(),
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    level: st.level,
    preset: services.keymap.preset,
    tasks: [...tasks],
    events,
    endedMs,
  });
  let log = snapshot();
  const notify = (): void => {
    log = { ...log, tasks: [...tasks], events, endedMs };
    save(log);
    for (const cb of listeners) cb();
  };
  const record = (kind: UsabilityEventKind, data: UsabilityEvent['data']): void => {
    if (!active) return;
    if (events.length >= MAX_EVENTS) events = events.slice(-Math.floor(MAX_EVENTS / 2));
    events = [...events, { ms: Math.round(now() - t0), kind, data }];
    notify();
  };
  const updateTask = (patch: Partial<TaskRecord>): void => {
    const cur = tasks[tasks.length - 1];
    if (cur === undefined || taskIndex < 0) return;
    tasks[tasks.length - 1] = { ...cur, ...patch };
  };

  // ---- document edits ----
  let offStore: Unsubscribe | null = null;
  const attachStore = (): void => {
    offStore?.();
    offStore = null;
    const store = services.store;
    if (store === null) return;
    offStore = store.subscribeCommits((txn) => {
      const command = txn.command ?? '';
      const kind: UsabilityEventKind = txn.hist.k === 'undo' ? 'undo' : txn.hist.k === 'redo' ? 'redo' : 'commit';
      const cur = tasks[tasks.length - 1];
      if (cur !== undefined && taskIndex >= 0) {
        if (kind === 'commit') updateTask({ commits: cur.commits + 1 });
        else if (kind === 'undo') updateTask({ undos: cur.undos + 1 });
      }
      record(kind, { command, label: txn.label.key });
    });
  };
  attachStore();
  offs.push(services.onChange(attachStore));
  offs.push(() => offStore?.());

  // ---- editor state ----
  offs.push(
    services.editor.subscribe((next, prev) => {
      if (next.workspace !== prev.workspace) record('workspace', { workspace: next.workspace });
      if (next.level !== prev.level) record('level', { level: next.level });
    }),
  );

  // ---- keys, clicks, errors ----
  if (win !== null) {
    const onKey = (e: KeyboardEvent): void => {
      if (e.repeat || isTextTarget(e.target)) return;
      if (['Control', 'Shift', 'Alt', 'Meta'].includes(e.key)) return;
      record('key', { chord: chordOfEvent(e) });
    };
    const onPointer = (e: PointerEvent): void => {
      const el = e.target instanceof Element ? e.target : null;
      const region = el?.closest('[data-region]')?.getAttribute('data-region') ?? null;
      const help = el?.closest('[data-help-id]')?.getAttribute('data-help-id') ?? null;
      record('click', { region, help, button: e.button });
    };
    const onError = (e: ErrorEvent): void => {
      record('error', { message: e.message.slice(0, 300) });
    };
    const onRejection = (e: PromiseRejectionEvent): void => {
      record('error', { message: String(e.reason).slice(0, 300) });
    };
    win.addEventListener('keydown', onKey, true);
    win.addEventListener('pointerdown', onPointer, true);
    win.addEventListener('error', onError);
    win.addEventListener('unhandledrejection', onRejection);
    offs.push(() => {
      win.removeEventListener('keydown', onKey, true);
      win.removeEventListener('pointerdown', onPointer, true);
      win.removeEventListener('error', onError);
      win.removeEventListener('unhandledrejection', onRejection);
    });
  }

  // ---- toasts: the text and kind of each toast the shell shows ([data-kind] items of its toast stack) ----
  if (win !== null && typeof MutationObserver !== 'undefined') {
    const seen = new WeakSet<Element>();
    const observer = new MutationObserver((records) => {
      for (const r of records) {
        for (const node of r.addedNodes) {
          if (!(node instanceof Element)) continue;
          const toast = node.matches('[data-kind][role="status"], [data-kind][role="alert"]')
            ? node
            : node.querySelector('[data-kind][role="status"], [data-kind][role="alert"]');
          if (toast === null || seen.has(toast)) continue;
          seen.add(toast);
          record('toast', { kind: toast.getAttribute('data-kind'), text: toast.textContent.slice(0, 200) });
        }
      }
    });
    observer.observe(win.document.body, { childList: true, subtree: true });
    offs.push(() => {
      observer.disconnect();
    });
  }

  // ---- automatic success checks (twice a second) ----
  const timer = setInterval(() => {
    const task = USABILITY_TASKS[taskIndex];
    const cur = tasks[tasks.length - 1];
    if (task === undefined || cur === undefined || cur.reachedMs !== null || cur.endMs !== null) return;
    const ok = ((): boolean => {
      try {
        return task.check(services, base);
      } catch {
        return false;
      }
    })();
    if (ok) {
      updateTask({ reachedMs: Math.round(now() - t0) });
      record('task', { task: task.id, event: 'reached' });
    }
  }, 500);
  offs.push(() => {
    clearInterval(timer);
  });

  const logger: UsabilityLogger = {
    get log() {
      return log;
    },
    get active() {
      return active;
    },
    get taskIndex() {
      return taskIndex;
    },
    startTask(index) {
      const task = USABILITY_TASKS[index];
      if (task === undefined || !active) return;
      taskIndex = index;
      // A copy: the store's document changes in place with every commit.
      const doc = services.store?.doc ?? null;
      base = { doc: doc === null ? null : snapshotDoc(doc), problems: problemCount(services) };
      tasks.push({
        id: task.id,
        startMs: Math.round(now() - t0),
        reachedMs: null,
        endMs: null,
        outcome: null,
        commits: 0,
        undos: 0,
      });
      record('task', { task: task.id, event: 'start' });
    },
    endTask(outcome) {
      const task = USABILITY_TASKS[taskIndex];
      if (task === undefined || !active) return;
      updateTask({ endMs: Math.round(now() - t0), outcome });
      record('task', { task: task.id, event: outcome });
      if (taskIndex + 1 < USABILITY_TASKS.length) logger.startTask(taskIndex + 1);
      else {
        taskIndex = -1;
        notify();
      }
    },
    stop() {
      if (!active) return;
      record('session', { event: 'stop' });
      endedMs = Math.round(now() - t0);
      active = false;
      for (const off of offs.splice(0).reverse()) off();
      notify();
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
  record('session', { event: 'start', profile });
  return logger;
}

// ---------------------------------------------------------------------------------------------------------------
// The session of this window
// ---------------------------------------------------------------------------------------------------------------

let current: UsabilityLogger | null = null;
const sessionListeners = new Set<() => void>();

export function usabilitySession(): UsabilityLogger | null {
  return current;
}

export function setUsabilitySession(next: UsabilityLogger | null): void {
  if (current !== null && current !== next) current.stop();
  current = next;
  for (const cb of sessionListeners) cb();
}

export function subscribeUsabilitySession(cb: () => void): Unsubscribe {
  sessionListeners.add(cb);
  return () => {
    sessionListeners.delete(cb);
  };
}
